import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

interface ProductPerformanceData {
  productId: string;
  profit: number;
  tradeCount: number;
  avgReturn: number;
}

interface ProductPerformanceChartProps {
  data: ProductPerformanceData[];
}

const ProductPerformanceChart: React.FC<ProductPerformanceChartProps> = ({ data }) => {
  // Sort by profit descending and take top 10
  const sortedData = [...data].sort((a, b) => b.profit - a.profit).slice(0, 10);

  // Color based on profit
  const getColor = (profit: number) => {
    if (profit > 0) return '#10b981'; // Green
    if (profit < 0) return '#ef4444'; // Red
    return '#64748b'; // Gray
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>{data.productId}</p>
          <p style={{ color: getColor(data.profit), fontWeight: 600 }}>
            Profit: {formatCurrency(data.profit)}
          </p>
          <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
            Trades: {data.tradeCount}
          </p>
          <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
            Avg Return: {(data.avgReturn * 100).toFixed(2)}%
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={sortedData} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis
          dataKey="productId"
          tick={{ fontSize: 12 }}
          angle={-15}
          textAnchor="end"
          height={60}
        />
        <YAxis
          tickFormatter={formatCurrency}
          tick={{ fontSize: 12 }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Bar dataKey="profit" radius={[8, 8, 0, 0]}>
          {sortedData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={getColor(entry.profit)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default ProductPerformanceChart;
